import React, { useState, useEffect, useRef } from 'react';

const SubscriptionModal = ({ isOpen, onClose, onSubmit, initialData = {}, mode }) => {
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [scansBespoke, setScansBespoke] = useState({ scans: '', bespokes: '' , storage: '' });

  // 🔹 Draggable position
  const [position, setPosition] = useState({ x: window.innerWidth / 2 - 250, y: 100 });
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState(null);
  const [timePeriod, setTimePeriod] = useState(null);
  const modalRef = useRef();

  useEffect(() => {
    fetch('https://cust.3dmu.prototechsolutions.com/prod/customer/subscription/getAllPlans')
      .then(res => res.json())
      .then(data => {
        data = data.body ? JSON.parse(data.body) : data;
        const augmentedData = [
          ...data,
          { id: 'payasyougo', name: 'payasyougo', type: 'payasyougo', description: 'Flexible token-based usage' },
          { id: 'subscription', name: 'subscription', type: 'subscription', description: 'Fixed monthly subscription' }
        ];

        setPlans(augmentedData);
        const types = augmentedData.map(plan => plan.name);
        setSubscriptionTypes(types);
      })
      .catch(err => console.error('Failed to fetch plans:', err));
  }, []);

  const [form, setForm] = useState({
    cid: '',
    expirydate: '',
    plan: '',
    subscribed_on: '',
    tokens: '',
    type: '',
    expired_on: '',
    comment: ''
  });

    // 🔹 Calculate timePeriod when dates in `form` change
  useEffect(() => {
    if (form.subscribed_on && form.expirydate) {
      const start = new Date(form.subscribed_on);
      const end = new Date(form.expirydate);

      if (!isNaN(start) && !isNaN(end) && end >= start) {
        const diff = Math.ceil(
          (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1; // include start day
        setTimePeriod(diff);
      } else {
        setTimePeriod(null);
      }
    }
  }, [form.subscribed_on, form.expirydate]);

  useEffect(() => {
    if (initialData) {
      setForm({
        cid: initialData.client_id || '',
        email: initialData.email || '',
        name: initialData.name || '',
        expirydate: initialData.expirydate?.slice(0, 10) || '',
        plan: initialData.plan || '',
        subscribed_on: initialData.subscribed_on?.slice(0, 10) || '',
        tokens: initialData.tokens || '',
        type: initialData.type || '',
        expired: initialData.expired || false,
        comment: initialData.comment || '',
        ...(mode === 'edit' && { tid: initialData.tid || '' })
      });
      // Update scans and bespokes based on the selected plan type
      const selectedPlan = plans.find(plan => plan.name === initialData.type);
      if (selectedPlan) {
        setScansBespoke({
          scans: selectedPlan.scan || 0,
          bespokes: selectedPlan.bespoke ?? 0,
          storage: selectedPlan.storage ?? 0
        });
      } else {
        setScansBespoke({ scans: 0, bespokes: 0, storage: 0 });
      }
    }
  }, [initialData, plans, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'type') {
      const selectedPlan = plans.find(plan => plan.name === value);
      if (selectedPlan) {
        const { scan, bespoke, storage } = selectedPlan;
        const calculatedTokens = scan * 5 + bespoke * 1;
        setScansBespoke({ scans: scan, bespokes: bespoke, storage });
        setForm(prev => ({ ...prev, tokens: calculatedTokens, type: value }));
      } else {
        setScansBespoke({ scans: '', bespokes: '', storage: '' });
        setForm(prev => ({ ...prev, tokens: '', type: value }));
      }
      return;
    }
    if (name === 'scans' || name === 'bespokes' || name === 'storage') {
      const newScansBespoke = { ...scansBespoke, [name]: Number(value) };
      setScansBespoke(newScansBespoke);
      const calculatedTokens = newScansBespoke.scans * 5 + newScansBespoke.bespokes * 1;
      setForm(prev => ({ ...prev, tokens: calculatedTokens }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = () => {
    const updatedForm = { ...form, expired: false };
    if (mode === 'add') delete updatedForm.tid;
    onSubmit(updatedForm);
    onClose();
  };

  // 🔹 Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest(".modal-header")) {
      setDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setRel({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - rel.x, y: e.clientY - rel.y });
  };

  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  if (!isOpen) return null;

  const modalStyle = {
    position: "fixed",
    top: position.y,
    left: position.x,
    width: "90%",
    maxWidth: "500px",
    background: "#f9f9f9",
    padding: "25px",
    border: "1px solid #ddd",
    borderRadius: "18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    zIndex: 1000,
    fontFamily: "Arial, sans-serif",
    overflowY: "auto",
    maxHeight: "90vh",
    cursor: dragging ? "grabbing" : "default",
    boxSizing: "border-box",
  };

  const responsiveStyles = {
    formContainer: { display: 'flex', flexDirection: 'column', gap: '14px' },
    fieldContainer: { display: 'flex', flexDirection: 'column', gap: '8px' },
    input: {
      width: '100%',
      padding: '8px',
      border: '1px solid #e0d2d2ff',
      borderRadius: '5px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      flexWrap: 'wrap'
    },
    column: {
      flex: "1 1 30%",   // roughly 3 per row
      minWidth: '120px' // ensures shrinking gracefully on small screens
    },
    buttonContainer: {
      marginTop: '25px',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      flexWrap: 'wrap'
    }
  };

  return (
    <div ref={modalRef} style={modalStyle} onMouseDown={handleMouseDown} className="modal-container">
      <div className="modal-header" style={{ cursor: "grab", fontWeight: "bold", marginBottom: "20px", color: "#333" }}>
        {mode === 'edit' ? 'Edit' : 'Add'} Subscription
      </div>

      <div style={responsiveStyles.formContainer}>
        {[{ label: 'Customer ID', name: 'cid', type: 'text' },
          { label: 'Name', name: 'name', type: 'text' },
          { label: 'Email', name: 'email', type: 'text' },
          { label: 'Subscription Plan', name: 'plan', type: 'text' },
          { label: 'Subscription Type', name: 'type', type: 'select', options: subscriptionTypes },
          { label: 'Comment', name: 'comment', type: 'text' }].map(({ label, name, type, options }) => (
          <div key={name} style={responsiveStyles.fieldContainer} className="field-container">
            <label htmlFor={name} style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>{label}</label>
            {type === 'select' ? (
              <select id={name} name={name} value={form[name] || ''} onChange={handleChange} style={responsiveStyles.input}>
                <option value="">Select {label}</option>
                {options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
              </select>
            ) : (
              <input id={name} name={name} type={type} value={form[name] ?? ''} onChange={handleChange} disabled={name === 'cid' || name === 'name' || name === 'email'} style={responsiveStyles.input} />
            )}
          </div>
        ))}

        {form.type === 'payasyougo' || form.type === 'subscription' ? (
          <div style={responsiveStyles.fieldContainer}>
            <label htmlFor="tokens" style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>Tokens</label>
            <input id="tokens" name="tokens" type="number" value={form.tokens || ''} onChange={handleChange} style={responsiveStyles.input} />
          </div>
        ) : (
            <div style={responsiveStyles.row} className="row">
              <div style={responsiveStyles.column} className="column">
                <label htmlFor="scans" style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>Scans</label>
                <input id="scans" name="scans" type="number" value={scansBespoke.scans === 0 ? '0' : scansBespoke.scans} onChange={handleChange} style={responsiveStyles.input} disabled />
              </div>
              <div style={responsiveStyles.column} className="column">
                <label htmlFor="bespokes" style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>Bespokes</label>
                <input id="bespokes" name="bespokes" type="number" value={scansBespoke.bespokes === 0 ? '0' : scansBespoke.bespokes} onChange={handleChange} style={responsiveStyles.input} disabled />
              </div>
              <div style={responsiveStyles.column} className="column">
                <label htmlFor="storage" style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>Storage</label>
                <input id="storage" name="storage" type="number" value={scansBespoke.storage === 0 ? '0' : scansBespoke.storage} onChange={handleChange} style={responsiveStyles.input} disabled />
              </div>
            </div>
        )}

        <div style={responsiveStyles.row}>
          {[{ label: 'Start Date', name: 'subscribed_on' }, { label: 'Expiry Date', name: 'expirydate' }].map(({ label, name }) => (
            <div key={name} style={responsiveStyles.column} className="column">
              <label htmlFor={name} style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 'bold', color: '#444' }}>{label}</label>
              <input id={name} name={name} type="date" value={form[name] || new Date().toISOString().split('T')[0]} onChange={handleChange} style={responsiveStyles.input} />
            </div>
          ))}
        </div>
        {timePeriod !== null && (
          <p style={{ fontSize: "14px", fontWeight: "500", marginTop: "8px", marginBottom: "12px", color: "#444" }}>
            Adding subscription for timeperiod of <strong>{timePeriod}</strong> days
          </p>
        )}

      </div>

      <div style={responsiveStyles.buttonContainer} className="button-container">
        <button onClick={handleSubmit} style={{ padding: '8px 16px', backgroundColor: '#007BFF', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', flex: '1 1 auto', minWidth: '100px' }}>Save</button>
        <button onClick={onClose} style={{ padding: '8px 16px', backgroundColor: '#e0e0e0', border: 'none', borderRadius: '5px', cursor: 'pointer', flex: '1 1 auto', minWidth: '100px' }}>Cancel</button>
      </div>
    </div>
  );
};
export default SubscriptionModal;
